/**
 * 개선사항 알림(§ whats-new) — 관리자가 도착하면 **한 번만** 뜨는 릴리스 요약.
 *
 * 읽기 전용 "이번에 이렇게 바뀌었습니다, [확인]" 패널이라 DS 의 명령형
 * confirm 다이얼로그를 쓴다 — 셸이 이미 <ConfirmDialogHost /> 를 1회 마운트해
 * 두었고(main.tsx), confirm 은 ESC·오버레이·확인으로 닫힌다. 확인이 곧 "봤음"
 * 이다(다시 보지 않기가 확인에 자동 적용된다).
 *
 * confirm 은 취소 버튼도 함께 그리지만(취소=false), 여기서는 확인/취소를
 * 가르지 않는다 — 어느 쪽으로 닫든 "봤음" 으로 저장한다. 되돌릴 조작이 아니라
 * 읽고 넘기는 알림이기 때문이다.
 *
 * 재노출 규칙: WHATS_NEW_VERSION 을 올리면 저장된 값과 달라져 다음 도착 때
 * 다시 뜬다. 뷰어·참가자에게는 띄우지 않는다 — 마운트 지점(AdminRoute)이
 * "관리자가 방금 왔다" 를 모호함 없이 정한다.
 */

import { useEffect, useRef } from 'react'
import { confirm } from '@cupia/ui/confirm-dialog'

/**
 * 이번 릴리스 식별자. **이 값을 올리면** 저장된 값과 달라져 알림이 다시 뜬다.
 * 날짜 문자열로 두어 "무엇이 담긴 릴리스인지" 를 사람이 읽을 수 있게 한다.
 */
export const WHATS_NEW_VERSION = '2026-08-08'

/** 로컬스토리지 키 — 마지막으로 확인한 릴리스 식별자를 담는다. */
export const WHATS_NEW_SEEN_KEY = `omnitrans:whatsnew:seen:${WHATS_NEW_VERSION}`

/**
 * 사용자 대상 변경점 — 전문 용어 없이 짧게. `title`(굵게) + `body` 로 나눠
 * 다이얼로그 본문(ReactNode)으로 렌더한다.
 */
export const WHATS_NEW_CHANGES: { title: string; body: string }[] = [
  {
    title: '양방향 통역 (ko ↔ vi)',
    body: '한 방에서 한국어·베트남어 어느 쪽으로 말해도 상대 언어로 통역됩니다. 방향 버튼에서 "양방향"을 고르세요.',
  },
  {
    title: '통역 누락 줄임',
    body: '길게 말한 발화가 조용히 사라지던 문제를 개선했습니다(자동 재시도).',
  },
  {
    title: '잘못된 자막 구분 표시',
    body: 'AI가 회의 발언 대신 엉뚱한 답변·반복을 낼 때 "추론됨 / 확인 필요" 배지로 구분해 보여줍니다.',
  },
  {
    title: '정당한 강조 반복 보존',
    body: '"네. 네."처럼 두 번 강조한 말이 하나로 줄던 문제를 고쳤습니다.',
  },
]

/** 로컬스토리지 접근은 예외로 감싼다 — private 모드·차단 환경에서 죽지 않게. */
function readSeen(): string | null {
  try {
    return localStorage.getItem(WHATS_NEW_SEEN_KEY)
  } catch {
    return null
  }
}

function markSeen(): void {
  try {
    localStorage.setItem(WHATS_NEW_SEEN_KEY, WHATS_NEW_VERSION)
  } catch {
    /* 저장 실패는 무시 — 다음에 또 뜰 뿐 기능은 살아 있다 */
  }
}

/** 다이얼로그 본문 — 변경점 목록(ReactNode). */
function Body(): React.ReactElement {
  return (
    <ul className="flex flex-col gap-3 text-start" data-testid="whats-new-body">
      {WHATS_NEW_CHANGES.map((c) => (
        <li key={c.title} className="flex flex-col gap-0.5">
          <span className="text-(length:--text-sm-size) font-semibold">{c.title}</span>
          <span className="text-(length:--text-sm-size) text-[var(--color-muted-foreground)]">
            {c.body}
          </span>
        </li>
      ))}
    </ul>
  )
}

/**
 * 개선사항 알림.
 *
 * `active` 는 "지금 이 사람에게 띄워도 되는가"(관리자가 도착했는가)를 뜻한다 —
 * 마운트 지점이 그 판단을 하고, 이 컴포넌트는 **한 번만** 뜨는 규칙만 든다.
 * 렌더 산출물은 없다(confirm 은 전역 호스트가 그린다).
 */
export function WhatsNew({ active = true }: { active?: boolean }): null {
  // 명령형 confirm 을 effect 에서 한 번만 띄운다 — StrictMode 이중 마운트·
  // active 재계산에도 중복으로 열리지 않게 ref 로 잠근다.
  const shownRef = useRef(false)

  useEffect(() => {
    if (!active || shownRef.current) return
    if (readSeen() === WHATS_NEW_VERSION) return
    shownRef.current = true
    // 확인/취소/ESC 어느 쪽으로 닫든 "봤음" 으로 저장한다(읽고 넘기는 알림).
    void confirm({
      title: '개선사항 알림',
      description: <Body />,
      confirmLabel: '확인',
      cancelLabel: '닫기',
    }).finally(markSeen)
  }, [active])

  return null
}
